import { LoadingSpinner } from "components/LoadingSpinner";
import { ModalAddressBooking } from "components/ModalAddressBooking";
import { useHotels } from "contexts/HotelsSearchContext/HotelProvider";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { FaWallet } from "react-icons/fa6";
import { fetchBookingConditions } from "services/fetch-booking-condition";
import { getHotelSelectedData } from "services/get-hotel-selected-data";
import type { Root as RootProfile } from "services/get-profile";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import { Formatter } from "utils/formatters";
import { getCookie } from "utils/functions-cookie";
import GuestsInput from "../GuestsInput";
import StayDatesRangeInput from "../StayDatesRangeInput";
import type { Room } from "../type";
import type { BookingCondition } from "./type";

type Props = {
  room: Room | null;
  checkInDate: string;
  rating: number;
  hotelId: number;
  token: string;
  setConditionsDataGlobal: (data: BookingCondition | null) => void;
  profile: RootProfile;
};

export const ListingStayDetailSidebar = ({
  room,
  rating,
  hotelId,
  token,
  setConditionsDataGlobal,
  profile,
  checkInDate,
}: Props) => {
  const [modalAddressBooking, setModalAddressBooking] = useState(false);
  const { setNumNights, loading, setLoading, isBooking, setIsBooking } =
    useHotels();

  const authToken = getCookie("auth_token");

  const handleClickCondition = async () => {
    setLoading(true);
    try {
      if (profile?.address === null) {
        setLoading(false);
        setModalAddressBooking(true);
        return;
      }

      if (!authToken || !room) {
        toast.error("Erro ao tentar buscar as condições de reserva");
        return 0;
      }

      const data = await fetchBookingConditions({
        hotelId: hotelId,
        roomId: [room.id],
        token: token,
        authToken: authToken,
        checkInDate,
      });

      if (!data) {
        toast.error(
          "Tivemos um problema ao buscar as condições de reserva. Tente novamente mais tarde."
        );
        return 0;
      }

      setIsBooking(true);
      setConditionsDataGlobal(data);
    } catch (error) {
      setLoading(false);
      setIsBooking(false);
      toast.error(
        "Tivemos um problema ao buscar as condições de reserva. Tente novamente mais tarde."
      );
    } finally {
      setLoading(false);

      setTimeout(() => {
        if (document.getElementById("reserve-container")) {
          document.getElementById("reserve-container")?.scrollIntoView({
            behavior: "smooth",
          });
        }
      }, 100);
    }

    if (isBooking) {
      setIsBooking(false);
      if (document.getElementById("topo")) {
        document.getElementById("topo")?.scrollIntoView({
          behavior: "smooth",
        });
      }
    }
  };

  const hotelSelected = getHotelSelectedData();

  useEffect(() => {
    setNumNights(hotelSelected?.numNights ?? 1);
  }, [hotelSelected?.numNights]);

  if (!room) {
    return null;
  }

  let buttonText;
  if (isBooking) {
    buttonText = "Cancelar";
  } else if (loading) {
    buttonText = <LoadingSpinner />;
  } else {
    buttonText = "Reservar";
  }

  if (!profile) {
    return null;
  }

  const isZero = profile.wallet.balance === 0;

  return (
    <>
      {modalAddressBooking && (
        <ModalAddressBooking setModalAddressBooking={setModalAddressBooking} />
      )}
      <div className="listingSectionSidebar__wrap shadow-xl">
        <form className="flex flex-col border border-neutral-200 dark:border-neutral-700 rounded-3xl ">
          <StayDatesRangeInput className="flex-1 z-[11]" />
          <div className="w-full border-b border-neutral-200 dark:border-neutral-700"></div>
          <GuestsInput className="flex-1" />
        </form>

        <div className="flex flex-col space-y-4">
          <p className="text-xl font-bold">Quarto</p>
          <p className="max-w-sm text-center">{room.description}</p>

          <div className="border-b border-neutral-200 dark:border-neutral-700"></div>
          <div className="flex items-center gap-x-4 justify-between font-semibold">
            <FaWallet size={25} className="self-center" />
            <span className="flex items-end text-xl">
              {" "}
              {Formatter.prettyPriceHotel(room.price, profile)}
            </span>
          </div>
          {profile.wallet.balance > 0
            ? room &&
              room.cashback.value > 0 && (
                <span className="text-base font-normal italic text-[#F97316] text-end">
                  Ganhe <strong>{`${room.cashback.value} ViroCash`}</strong> de
                  volta!
                </span>
              )
            : null}
        </div>

        {profile.wallet.balance > 0 || profile.subscription?.active ? (
          <ButtonPrimary
            className={`${
              isBooking
                ? "bg-[--yellow] hover:bg-[--yellow-dark] "
                : "bg-[--blue] hover:bg-[--blue-dark]"
            }   transition-colors duration-300 ease-in-out
                disabled:bg-[#b2d0e0] disabled:cursor-not-allowed
              `}
            onClick={handleClickCondition}
            disabled={isZero || loading}
          >
            {buttonText}
          </ButtonPrimary>
        ) : (
          <ButtonPrimary
            href={"/plans"}
            className="bg-[--blue] hover:bg-[--blue-dark]"
          >
            Faça sua assinatura
          </ButtonPrimary>
        )}
      </div>
    </>
  );
};
